import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { opsColors } from '../../../../theme/opsTokens';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip);

interface SparklineProps {
  values: number[];
  color?: string;
  width?: number;
  height?: number;
  ariaLabel?: string;
}

/** Tiny inline trend line — no axes, no legend, no tooltip. */
const Sparkline: React.FC<SparklineProps> = ({
  values,
  color,
  width = 120,
  height = 40,
  ariaLabel,
}) => {
  const lineColor = color ?? opsColors.brand.primary;

  const data = useMemo(() => ({
    labels: values.map((_, i) => i.toString()),
    datasets: [
      {
        data: values,
        borderColor: lineColor,
        backgroundColor: alpha(lineColor, 0.18),
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 1.5,
      },
    ],
  }), [values, lineColor]);

  const options = useMemo(() => ({
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      x: { display: false },
      y: { display: false },
    },
    elements: {
      line: { borderJoinStyle: 'round' as const },
    },
  }), []);

  if (values.length === 0) {
    return <Box sx={{ width, height }} aria-hidden />;
  }

  return (
    <Box sx={{ width, height }} role={ariaLabel ? 'img' : undefined} aria-label={ariaLabel}>
      <Line data={data} options={options} width={width} height={height} />
    </Box>
  );
};

export default React.memo(Sparkline);
